import React, { useCallback, useEffect } from 'react';

import { defineMessages, useIntl } from 'react-intl';
import { di } from 'react-magnetic-di';
import { graphql, useFragment } from 'react-relay';

import { fg } from '@atlaskit/platform-feature-flags';
import { Text } from '@atlaskit/primitives/compiled';
import { useAnalytics } from '@atlassian/conversation-assistant-instrumentation/controllers/analytics';

import type { UpdateWorkItemAllFieldTypesAction } from '../../../../../common/types';
import { FieldTooltipContent } from '../../../../../common/ui/FieldTooltipContent';
import {
	type RegisterTooltipProvider,
	useRegisterTooltipContentProvider,
} from '../../../../../common/utils/useRegisterTooltipContentProvider';
import { getActionData, toReportableFieldType } from '../../../utils';

import type { genericField_updateWorkItemAllFieldTypesActionRenderer$key } from './__generated__/genericField_updateWorkItemAllFieldTypesActionRenderer.graphql';

const messages = defineMessages({
	updateAction: {
		id: 'update-work-item-all-fields-action-renderer.ui.action-card.generic-field.updateAction',
		defaultMessage: 'Update {fieldName} to {fieldValue}',
		description: 'Label for the change field field',
	},
	clearAction: {
		id: 'update-work-item-all-fields-action-renderer.ui.action-card.generic-field.clearAction',
		defaultMessage: 'Clear {fieldName}',
		description:
			'Accessible label for the button that clears the current value of a generic field in a Jira work item action card.',
	},
});

function sanitizeFieldName(value: unknown) {
	if (typeof value === 'string') {
		return value?.trim()?.toLowerCase();
	}
	return String(value);
}

function sanitizeFieldValue(value: unknown) {
	if (typeof value === 'string') {
		return value?.trim();
	}
	if (Array.isArray(value)) {
		return value?.join(', ');
	}
	return String(value);
}

function isObject(value: unknown): boolean {
	return typeof value === 'object' && value !== null;
}

function hasRenderableValue(value: unknown): boolean {
	if (Array.isArray(value)) {
		return !value.some(isObject);
	}
	return !isObject(value);
}

export type Props = {
	queryRef: genericField_updateWorkItemAllFieldTypesActionRenderer$key;
	action: UpdateWorkItemAllFieldTypesAction;
	registerTooltipProvider?: RegisterTooltipProvider;
};

export const GenericField = ({
	queryRef,
	action,
	registerTooltipProvider,
}: Props): React.JSX.Element | null => {
	di(useAnalytics);

	const { formatMessage } = useIntl();
	const { sendAnalyticsEvent } = useAnalytics();

	const { fieldKey, fieldValue, fieldReasoning } = getActionData(action);

	const data = useFragment(
		graphql`
			fragment genericField_updateWorkItemAllFieldTypesActionRenderer on JiraIssueField {
				fieldId
				name
			}
		`,
		queryRef,
	);

	const isFieldRenderable = data.fieldId === fieldKey;
	const isUpdateAction = isFieldRenderable && !!fieldValue;
	const isUnrenderableUpdate = isUpdateAction && !hasRenderableValue(fieldValue);

	useEffect(() => {
		if (isUnrenderableUpdate && fg('jira-track-invalid-update-workitem-actions')) {
			sendAnalyticsEvent({
				eventType: 'operational',
				action: 'detected',
				actionSubject: 'invalidField',
				actionSubjectId: 'updateWorkActionRenderer',
				attributes: {
					fieldType: toReportableFieldType(fieldKey),
					renderer: 'generic',
					anomaly: 'objectValue',
					invocationId: action.invocationId,
				},
			});
		}
	}, [isUnrenderableUpdate, fieldKey, action.invocationId, sendAnalyticsEvent]);

	const formattedMessage = isUpdateAction
		? formatMessage(messages.updateAction, {
				fieldName: sanitizeFieldName(data?.name),
				fieldValue: sanitizeFieldValue(fieldValue),
			})
		: formatMessage(messages.clearAction, {
				fieldName: sanitizeFieldName(data?.name),
			});

	const toolTipContentProvider = useCallback(
		() => (
			<FieldTooltipContent
				reasoning={fieldReasoning}
				fieldName={data?.name}
				suggested={fieldValue !== undefined && fieldValue !== null ? String(fieldValue) : undefined}
			/>
		),
		[fieldValue, fieldReasoning, data?.name],
	);

	useRegisterTooltipContentProvider({
		registerTooltipProvider,
		fieldId: fieldKey,
		provider: toolTipContentProvider,
		enabled: isFieldRenderable,
	});

	if (!isFieldRenderable || !data?.name) {
		return null;
	}

	return (
		<Text
			color="color.text.subtle"
			maxLines={1}
			testId="update-work-item-all-fields-action-renderer.ui.action-card.card-fields-list.generic-field"
		>
			{formattedMessage}
		</Text>
	);
};
